// Phase 0093-A — Live Study Capture dispatcher.
//
// Reads the template's `capture_mode` and routes to the right capture
// component. Until Phase 0093-B/C/D/E ship, every mode other than
// TIMER_PLUS_COUNTER / COUNTER / TIMER (handled by LiveStudyCapturePage)
// renders a "coming soon" placeholder rather than a broken screen.
//
// Also implements **session recovery**: on mount, if the session is
// IN_PROGRESS or PAUSED, we fetch /sessions/:id/recover and rebuild
// local state from the server-stamped timer + counter events. The user
// resumes exactly where they left off after a tab close / network drop /
// device crash.

import { useEffect, useState } from 'react'
import { App, Alert, Button, Card, Result, Skeleton, Space, Tag, Typography } from 'antd'
import { LeftOutlined, ReloadOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { liveStudyApi } from '@/features/hr/liveStudyApi'
import LiveStudyCapturePage from './LiveStudyCapturePage'
// Phase 0093-B
import AutoFetchSummaryCapture from './AutoFetchSummaryCapture'
// Ring Frame Time & Motion Phase 1 (Tasks 8-9)
import RingFrameStudyCapturePage from './RingFrameStudyCapturePage'

const { Text, Title } = Typography

// Capture modes that have a dedicated component built. Phase 0093-A
// shipped timer/counter family + the modes wired in LiveStudyCapturePage's
// observation panel. Phase 0093-B ships AUTO_FETCH_SUMMARY.
const IMPLEMENTED_MODES = new Set([
  'TIMER',
  'COUNTER',
  'TIMER_PLUS_COUNTER',
  'WORK_SAMPLING',
  'CHECKLIST',
  'SCORE_1_TO_5',
  'YES_NO',
  'AUTO_FETCH_SUMMARY',
  'RING_FRAME_TIME_MOTION',
])

// Friendly names for the "coming soon" placeholder.
const MODE_LABELS: Record<string, string> = {
  TIME_AND_MOTION: 'Time & Motion Study',
  BEFORE_AFTER: 'Before / After Study',
  RCA: 'Root-Cause Analysis',
  PHOTO_EVIDENCE: 'Photo Evidence',
  MULTI_OBSERVATION: 'Multi-Observation Study',
  CUSTOM: 'Custom Study',
  OBSERVATION: 'Observation Study',
}

// Local-storage key for session-recovery hints. We keep just the
// session_id + last_autosave_at as a breadcrumb; full state is reconstructed
// from the server's append-only event log.
const RECOVERY_KEY = 'live_study_inflight_session'

export function writeRecoveryBreadcrumb(sessionId: string, lastAutosaveAt: string | null) {
  try {
    localStorage.setItem(RECOVERY_KEY, JSON.stringify({
      sessionId,
      lastAutosaveAt,
      writtenAt: new Date().toISOString(),
    }))
  } catch {
    // localStorage may be disabled (private mode); recovery still works
    // via /sessions/:id/recover endpoint as long as the user navigates
    // back to the session URL.
  }
}

export function clearRecoveryBreadcrumb() {
  try { localStorage.removeItem(RECOVERY_KEY) } catch { /* noop */ }
}

export function readRecoveryBreadcrumb(): { sessionId: string; lastAutosaveAt: string | null } | null {
  try {
    const raw = localStorage.getItem(RECOVERY_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (typeof parsed?.sessionId !== 'string') return null
    return parsed
  } catch {
    return null
  }
}

export default function LiveStudyCaptureRouter() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const { message } = App.useApp()
  const [recovered, setRecovered] = useState(false)

  const { data: recovery, isLoading } = useQuery({
    queryKey: ['study-session-recover', sessionId],
    queryFn: () => liveStudyApi.recoverSession(sessionId!),
    enabled: !!sessionId,
    // Don't poll — this is a one-shot rebuild on mount.
    staleTime: Infinity,
    retry: 1,
  })

  // On successful recovery of a resumable session, surface a banner once.
  useEffect(() => {
    if (recovery?.resumable && !recovered) {
      writeRecoveryBreadcrumb(sessionId!, recovery.last_autosave_at)
      if (recovery.tap_count > 0 || recovery.timer_events.length > 0) {
        message.info(
          `Session resumed at ${recovery.last_autosave_at ?? 'unknown time'} — ` +
          `${recovery.tap_count} taps captured.`,
          5,
        )
      }
      setRecovered(true)
    }
  }, [recovery, recovered, sessionId, message])

  if (isLoading) {
    return <Card style={{ margin: 24 }}><Skeleton active /></Card>
  }

  if (!recovery) {
    return (
      <Result
        status="404"
        title="Session not found"
        subTitle="The study session may have been deleted or the link is wrong."
        extra={
          <Button onClick={() => navigate('/hr/live-studies')}>
            <LeftOutlined /> Back to Live Studies
          </Button>
        }
      />
    )
  }

  const mode = recovery.template?.capture_mode ?? ''

  // Phase 0093-B — auto-fetch summary capture.
  if (mode === 'AUTO_FETCH_SUMMARY') {
    return <AutoFetchSummaryCapture />
  }

  // Ring Frame Time & Motion Phase 1 (Tasks 8-9) — round-based RHS/LHS
  // capture with MEND toggle, break events and breakage computation.
  if (mode === 'RING_FRAME_TIME_MOTION') {
    return <RingFrameStudyCapturePage />
  }

  // Modes wired in LiveStudyCapturePage (timer/counter family) → render it.
  if (mode === 'TIMER_PLUS_COUNTER' || mode === 'TIMER' || mode === 'COUNTER') {
    return <LiveStudyCapturePage />
  }

  // All other modes show a "Phase coming" placeholder. Show enough state
  // that the IE engineer knows the session and recovery worked, just that
  // the capture UI for this template type ships in a later phase.
  if (!IMPLEMENTED_MODES.has(mode)) {
    return (
      <Card style={{ margin: 24, maxWidth: 720 }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Button
            type="text"
            icon={<LeftOutlined />}
            onClick={() => navigate('/hr/live-studies')}
          >
            Back to Live Studies
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            {recovery.template?.template_name ?? 'Study session'}
          </Title>
          <Space wrap>
            <Tag color="blue">Mode: {mode || 'UNKNOWN'}</Tag>
            <Tag color="gold">
              Capture UI: {MODE_LABELS[mode] ?? mode} — coming soon
            </Tag>
            <Tag>Session: {recovery.session?.session_no ?? '—'}</Tag>
          </Space>
          <Alert
            type="info"
            showIcon
            message="This study type uses a capture UI that ships in a later phase."
            description={
              <>
                The session is created and tracked on the server. Phase 0093-B
                ships the auto-fetch summary capture; 0093-C ships checklist
                and photo capture; 0093-D ships before/after; 0093-E ships
                Time Study + Motion + RCA.
                <br />
                <Text type="secondary">
                  Until then, this session will remain in DRAFT. Mode:{' '}
                  <code>{mode || 'UNKNOWN'}</code>.
                </Text>
              </>
            }
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => window.location.reload()}
          >
            Reload
          </Button>
        </Space>
      </Card>
    )
  }

  // Implemented but not timer/counter — fall through to LiveStudyCapturePage
  // (which currently handles work-sampling / checklist / score / yes-no via
  // its observation panel). Future phases may split these out.
  return <LiveStudyCapturePage />
}
